import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { SEO } from "@/components/SEO"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Slider } from "@/components/ui/slider"
import { ArrowDown, ArrowLeft, Wallet, Loader2, AlertCircle, ExternalLink, Zap, Clock, History, CheckCircle2, Trash2 } from "lucide-react"
import welephantLogo from "@/assets/tokens/welephant.svg"
import baseLogo from "@/assets/tokens/base.svg"
import bnbLogo from "@/assets/tokens/bnb.svg"
import { useEthersContext } from "@/providers/EthersProvider"
import { useAuxEthersContext } from "@/providers/AuxEthersProvider"
import { useTransactionToast } from "@/hooks/useTransactionToast"
import { useWormholeBridge, BridgeQuote, BRIDGE_SPENDER_ADDRESS } from "@/hooks/useWormholeBridge"
import { useTokenValue } from "@/hooks/useTokenPrice"
import { useTokenBalance } from "@/hooks/useChainProvider"
import { formatUnits, parseUnits, Contract, InterfaceAbi, Signer, ContractTransactionResponse } from "ethers"
import { WELEPHANT_DECIMALS, formatTokenAmount } from "@/lib/ethersUtils"
import { cn } from "@/lib/utils"
import {
  ChainConfig,
  BASE_CHAIN,
  BNB_CHAIN,
  WELEPHANT_ADDRESSES,
  getAddChainParams,
  chainIdToHex,
} from "@/lib/chains"
import { getWormholeTxStatus, WormholeStatus } from "@/lib/wormholescan-status"
import { isUserRejection, getWalletErrorMessage, USER_REJECTED_MESSAGE, logContractError } from "@/lib/walletErrors"
import { isPageVisible } from "@/lib/pageVisibility"
import { validateBalance, isAmountExceedingBalance } from "@/lib/validateBalance"

// ERC20 ABI for token operations
const ERC20_ABI = [
  "function balanceOf(address owner) view returns (uint256)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function decimals() view returns (uint8)",
] as const

// WELEPHANT decimals


// History storage
const BRIDGE_HISTORY_KEY = 'welephant_bridge_history'

interface BridgeHistoryItem {
  txHash: string
  amount: string // formatted amount string
  sourceChainId: number
  destChainId: number
  timestamp: number
}

function loadHistory(): BridgeHistoryItem[] {
  if (typeof window === 'undefined') return []
  const stored = localStorage.getItem(BRIDGE_HISTORY_KEY)
  if (stored) {
    try {
      return JSON.parse(stored)
    } catch {
      return []
    }
  }
  return []
}

function saveHistory(history: BridgeHistoryItem[]): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(BRIDGE_HISTORY_KEY, JSON.stringify(history))
  }
}

function addToHistory(item: BridgeHistoryItem): void {
  const history = loadHistory()
  // Add to beginning, avoid duplicates
  const filtered = history.filter(h => h.txHash !== item.txHash)
  const updated = [item, ...filtered].slice(0, 50) // Keep last 50
  saveHistory(updated)
}

export function BridgePage() {
  const { provider: primaryProvider, signer: primarySigner, address, isConnected, chainId: primaryChainId, balance: primaryBalance } = useEthersContext()
  const auxContext = useAuxEthersContext()
  const { showTxPending, showTxError } = useTransactionToast()
  const wormholeBridge = useWormholeBridge()

  // View state
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState<BridgeHistoryItem[]>([])

  // Chain selection state
  const [sourceChain, setSourceChain] = useState<ChainConfig>(BASE_CHAIN)
  const [destChain, setDestChain] = useState<ChainConfig>(BNB_CHAIN)

  // Amount state
  const [inputAmount, setInputAmount] = useState("")
  const [amountPercent, setAmountPercent] = useState(0)
  const isUpdatingFromSlider = useRef(false)

  // Loading states
  const [isApproving, setIsApproving] = useState(false)
  const [isBridging, setIsBridging] = useState(false)

  // Allowance state
  const [allowance, setAllowance] = useState<bigint>(0n)

  // Quote state
  const [quote, setQuote] = useState<BridgeQuote | null>(null)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // Wormhole status polling for pending transfer
  const [wormholeStatus, setWormholeStatus] = useState<WormholeStatus>("unknown")
  const statusPollRef = useRef<NodeJS.Timeout | null>(null)

  // Determine which provider/signer to use based on source chain
  const isOnSourceChain = primaryChainId === sourceChain.id
  const activeSigner = isOnSourceChain ? primarySigner : auxContext.signer
  const activeProvider = isOnSourceChain ? primaryProvider : auxContext.provider
  const activeChainId = isOnSourceChain ? primaryChainId : auxContext.chainId
  const activeNativeBalance = isOnSourceChain ? primaryBalance : auxContext.balance

  // Load history on mount
  useEffect(() => {
    setHistory(loadHistory())
  }, [])

  // Track if we've already saved the current transfer to history
  const savedTxHashRef = useRef<string | null>(null)

  // Save to history and start status polling when transfer is submitted
  useEffect(() => {
    const transfer = wormholeBridge.pendingTransfer
    if (transfer?.status === 'submitted' && transfer.txHash && savedTxHashRef.current !== transfer.txHash) {
      // Save to history immediately
      addToHistory({
        txHash: transfer.txHash,
        amount: formatUnits(transfer.amount, WELEPHANT_DECIMALS),
        sourceChainId: transfer.sourceChain.id,
        destChainId: transfer.destChain.id,
        timestamp: transfer.createdAt,
      })
      setHistory(loadHistory())
      savedTxHashRef.current = transfer.txHash
      // Reset status for new transfer
      setWormholeStatus("unknown")
    }
  }, [wormholeBridge.pendingTransfer?.status, wormholeBridge.pendingTransfer?.txHash])

  // Poll Wormhole status every 15 seconds
  useEffect(() => {
    const transfer = wormholeBridge.pendingTransfer
    if (!transfer?.txHash) {
      // Clear polling when no pending transfer
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current)
        statusPollRef.current = null
      }
      setWormholeStatus("unknown")
      return
    }

    // Fetch status immediately
    const fetchStatus = async () => {
      if (!isPageVisible()) return
      try {
        const result = await getWormholeTxStatus(transfer.txHash!)
        setWormholeStatus(result.status)
      } catch (err) {
        console.error('Failed to fetch Wormhole status:', err)
      }
    }

    fetchStatus()

    // Poll every 15 seconds
    statusPollRef.current = setInterval(fetchStatus, 15000)

    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current)
        statusPollRef.current = null
      }
    }
  }, [wormholeBridge.pendingTransfer?.txHash])

  // Clear pending transfer handler
  const handleClearPending = useCallback(() => {
    wormholeBridge.clearPendingTransfer()
    savedTxHashRef.current = null
    setWormholeStatus("unknown")
  }, [wormholeBridge])

  // Create contract instances for source chain
  const sourceWelephantContract = useMemo(() => {
    if (!activeProvider) return null
    const tokenAddress = WELEPHANT_ADDRESSES[sourceChain.id]
    if (!tokenAddress) return null
    return new Contract(tokenAddress, ERC20_ABI as InterfaceAbi, activeProvider)
  }, [activeProvider, sourceChain.id])

  // Parse input amount to bigint for USD calculation
  const inputAmountWei = useMemo(() => {
    if (!inputAmount || parseFloat(inputAmount) <= 0) return null
    try {
      return parseUnits(inputAmount, WELEPHANT_DECIMALS)
    } catch {
      return null
    }
  }, [inputAmount])

  // Get USD value using token price service (source chain)
  const { formatted: inputUsdFormatted, loading: usdLoading } = useTokenValue(
    'WELEPHANT',
    sourceChain.id,
    inputAmountWei,
    WELEPHANT_DECIMALS
  )

  // Get USD value for destination chain
  const { formatted: destUsdFormatted, loading: destUsdLoading } = useTokenValue(
    'WELEPHANT',
    destChain.id,
    inputAmountWei,
    WELEPHANT_DECIMALS
  )

  // Fetch balances using public RPC (works regardless of wallet connection)
  const { balance: sourceBalance } = useTokenBalance(
    sourceChain.id,
    WELEPHANT_ADDRESSES[sourceChain.id],
    address,
    WELEPHANT_DECIMALS
  )

  // Check allowance
  useEffect(() => {
    const checkAllowance = async () => {
      if (!sourceWelephantContract || !address || activeChainId !== sourceChain.id) return

      try {
        const currentAllowance = await sourceWelephantContract.allowance(address, BRIDGE_SPENDER_ADDRESS)
        setAllowance(currentAllowance)
      } catch (err) {
        console.error('Failed to check allowance:', err)
      }
    }

    checkAllowance()
  }, [sourceWelephantContract, address, activeChainId, sourceChain.id])

  // Get quote when amount changes
  useEffect(() => {
    const fetchQuote = async () => {
      if (!inputAmount || parseFloat(inputAmount) <= 0) {
        setQuote(null)
        return
      }

      try {
        const amountWei = parseUnits(inputAmount, WELEPHANT_DECIMALS)
        // Pass signer for accurate destination amount estimation
        const newQuote = await wormholeBridge.getQuote(
          sourceChain,
          destChain,
          amountWei,
          false,
          activeSigner || undefined
        )
        setQuote(newQuote)
      } catch (err) {
        console.error('Failed to get quote:', err)
      }
    }

    const timer = setTimeout(fetchQuote, 500)
    return () => clearTimeout(timer)
  }, [inputAmount, sourceChain, destChain, wormholeBridge.getQuote, activeSigner])


  // Update slider when input changes manually
  useEffect(() => {
    if (isUpdatingFromSlider.current) {
      isUpdatingFromSlider.current = false
      return
    }
    if (!inputAmount || sourceBalance === 0n) {
      setAmountPercent(0)
      return
    }
    try {
      const inputWei = parseUnits(inputAmount, WELEPHANT_DECIMALS)
      const percent = Number((inputWei * 100n) / sourceBalance)
      setAmountPercent(Math.min(100, Math.max(0, percent)))
    } catch {
      // Invalid input
    }
  }, [inputAmount, sourceBalance])

  // Handle slider change
  const handleAmountSliderChange = useCallback((percent: number) => {
    setAmountPercent(percent)
    if (sourceBalance === 0n) {
      setInputAmount("")
      return
    }
    isUpdatingFromSlider.current = true
    const amountWei = (sourceBalance * BigInt(percent)) / 100n
    setInputAmount(formatUnits(amountWei, WELEPHANT_DECIMALS))
  }, [sourceBalance])

  // Toggle source/dest chains
  const toggleChains = () => {
    const newSource = destChain
    const newDest = sourceChain
    setSourceChain(newSource)
    setDestChain(newDest)
    setInputAmount("")
    setQuote(null)
    setError(null)
  }

  // Set max input
  const setMaxInput = () => {
    setInputAmount(formatUnits(sourceBalance, WELEPHANT_DECIMALS))
  }

  // Switch wallet to source chain
  const switchToSourceChain = async () => {
    if (!window.ethereum) return

    try {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdToHex(sourceChain.id) }],
        })
      } catch (switchError: unknown) {
        const err = switchError as { code?: number }
        if (err.code === 4902) {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [getAddChainParams(sourceChain)],
          })
        } else {
          throw switchError
        }
      }
    } catch (err) {
      console.error('Failed to switch chain:', err)
      setError('Failed to switch network')
    }
  }

  // Handle approval
  const handleApprove = async () => {
    if (!activeSigner || !sourceWelephantContract) return

    // Pre-flight gas check
    const validation = validateBalance({ balance: activeNativeBalance })
    if (!validation.valid) {
      showTxError("Approve", validation.error!)
      return
    }

    setIsApproving(true)
    setError(null)

    try {
      const inputWei = parseUnits(inputAmount, WELEPHANT_DECIMALS)
      const connectedContract = sourceWelephantContract.connect(activeSigner as Signer) as Contract & {
        approve: {
          estimateGas(spender: string, amount: bigint): Promise<bigint>
          (spender: string, amount: bigint): Promise<ContractTransactionResponse>
        }
      }
      const gasEstimate = await connectedContract.approve.estimateGas(BRIDGE_SPENDER_ADDRESS, inputWei)
      const gasLimit = gasEstimate + gasEstimate / 10n // +10%
      const tx = await connectedContract.approve(BRIDGE_SPENDER_ADDRESS, inputWei, { gasLimit })
      const txToast = showTxPending("Approve", tx.hash)
      await tx.wait()
      txToast.success()
      setAllowance(inputWei)
    } catch (err: unknown) {
      logContractError("Bridge.approve", err)
      if (isUserRejection(err)) {
        showTxError("Approve", USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Approval failed")
        showTxError("Approve", errorMessage || "Approval failed")
        setError(errorMessage)
      }
    } finally {
      setIsApproving(false)
    }
  }

  // Handle bridge
  const handleBridge = async () => {
    if (!activeSigner || !address) return

    // Pre-flight balance check (gas + token)
    const inputWei = parseUnits(inputAmount, WELEPHANT_DECIMALS)
    const validation = validateBalance({
      balance: activeNativeBalance,
      tokenBalance: sourceBalance,
      tokenAmount: inputWei,
      tokenSymbol: "WELEPHANT",
      tokenDecimals: WELEPHANT_DECIMALS,
    })
    if (!validation.valid) {
      showTxError("Bridge", validation.error!)
      return
    }

    setIsBridging(true)
    setError(null)

    try {

      const transfer = await wormholeBridge.bridge(
        sourceChain,
        destChain,
        inputWei,
        activeSigner,
        address,
        false
      )

      if (transfer?.status === 'submitted') {
        showTxPending("Bridge", transfer.txHash || '')
        setInputAmount("")
        setQuote(null)
      } else if (transfer?.status === 'failed') {
        setError(transfer.error || 'Bridge failed')
        showTxError("Bridge", transfer.error || 'Bridge failed')
      }
    } catch (err: unknown) {
      logContractError("Bridge.bridge", err)
      if (isUserRejection(err)) {
        showTxError("Bridge", USER_REJECTED_MESSAGE)
      } else {
        const errorMessage = getWalletErrorMessage(err, "Bridge failed")
        showTxError("Bridge", errorMessage || "Bridge failed")
        setError(errorMessage)
      }
    } finally {
      setIsBridging(false)
    }
  }

  // Check if approval is needed
  const needsApproval = useMemo(() => {
    if (!inputAmount || parseFloat(inputAmount) <= 0) return false
    try {
      return parseUnits(inputAmount, WELEPHANT_DECIMALS) > allowance
    } catch {
      return false
    }
  }, [inputAmount, allowance])

  // Check if we're on the wrong chain
  const needsChainSwitch = isConnected && activeChainId !== sourceChain.id

  // Can bridge check
  const canBridge = isConnected &&
    inputAmount &&
    parseFloat(inputAmount) > 0 &&
    !needsApproval &&
    !needsChainSwitch &&
    !isBridging &&
    !wormholeBridge.isQuoting

  return (
    <>
      <SEO
        title="Bridge"
        url="/bridge"
        description="Bridge WELEPHANT tokens between Base and BNB Chain using Wormhole. Secure, fast cross-chain transfers with automatic relaying."
        keywords="token bridge, cross-chain, Wormhole, Base to BNB, WELEPHANT bridge"
      />
      <div className="container py-16">
      <div className="mx-auto max-w-md">
        <Card className="overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            {showHistory ? (
              <>
                <button
                  onClick={() => setShowHistory(false)}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <h2 className="text-lg font-semibold">Bridge History</h2>
                {history.length > 0 ? (
                  <button
                    onClick={() => {
                      saveHistory([])
                      setHistory([])
                    }}
                    className="rounded-lg p-2 hover:bg-muted transition-colors text-muted-foreground hover:text-destructive"
                    title="Clear history"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : (
                  <div className="w-8" />
                )}
              </>
            ) : (
              <>
                <h2 className="text-lg font-semibold">Bridge WELEPHANT</h2>
                <button
                  onClick={() => setShowHistory(true)}
                  className="rounded-lg p-2 hover:bg-muted transition-colors"
                  title="History"
                >
                  <History className="h-5 w-5" />
                </button>
              </>
            )}
          </div>

          {showHistory ? (
            <CardContent className="p-4">
              <BridgeHistoryView history={history} />
            </CardContent>
          ) : (
            <CardContent className="p-4 space-y-2">
              {/* From Section */}
              <div className={cn("rounded-xl bg-muted/50 p-4", isAmountExceedingBalance(inputAmount, sourceBalance, WELEPHANT_DECIMALS) && "bg-destructive/10 border border-destructive/30")}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">From</span>
                  <ChainDisplay chain={sourceChain} />
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <TokenBadge />
                  <div className="flex-1" />
                  <Slider
                    value={[amountPercent]}
                    onValueChange={([value]) => handleAmountSliderChange(value)}
                    min={0}
                    max={100}
                    step={1}
                    className="w-24"
                  />
                  <span className="text-sm text-primary w-10 text-right">{amountPercent}%</span>
                </div>

                <div className="mb-2">
                  <Input
                    type="number"
                    placeholder="0"
                    value={inputAmount}
                    onChange={(e) => setInputAmount(e.target.value)}
                    className="text-2xl !border-0 bg-transparent p-0 h-auto text-right"
                  />
                </div>

                <div className="flex justify-between">
                  <button
                    onClick={setMaxInput}
                    className={cn("flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors", isAmountExceedingBalance(inputAmount, sourceBalance, WELEPHANT_DECIMALS) && "text-destructive")}
                  >
                    <Wallet className="h-3 w-3" />
                    {formatTokenAmount(sourceBalance, WELEPHANT_DECIMALS)}
                  </button>
                  {inputAmount && parseFloat(inputAmount) > 0 && (
                    <span className={cn(
                      "text-sm text-muted-foreground",
                      usdLoading && "animate-pulse"
                    )}>
                      {inputUsdFormatted}
                    </span>
                  )}
                </div>
              </div>

              {/* Swap Direction Button */}
              <div className="flex justify-center -my-1 relative z-10">
                <button
                  onClick={toggleChains}
                  className="rounded-full border-4 border-background bg-muted p-2 hover:bg-muted/80 transition-colors"
                >
                  <ArrowDown className="h-4 w-4" />
                </button>
              </div>

              {/* To Section */}
              <div className="rounded-xl bg-muted/50 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">To</span>
                  <ChainDisplay chain={destChain} />
                </div>

                <div className="flex items-center gap-2 mb-3">
                  <TokenBadge />
                </div>

                <div className={cn(
                  "text-2xl font-medium mb-2 text-right",
                  wormholeBridge.isQuoting && "animate-pulse"
                )}>
                  {wormholeBridge.isQuoting ? (
                    <Loader2 className="h-6 w-6 animate-spin ml-auto" />
                  ) : quote ? (
                    formatTokenAmount(quote.destAmount, WELEPHANT_DECIMALS)
                  ) : (
                    "0"
                  )}
                </div>

                <div className="flex justify-between">
                  <span className="text-xs text-muted-foreground">
                    You will receive
                  </span>
                  {quote && inputAmount && parseFloat(inputAmount) > 0 && (
                    <span className={cn(
                      "text-sm text-muted-foreground",
                      destUsdLoading && "animate-pulse"
                    )}>
                      {destUsdFormatted}
                    </span>
                  )}
                </div>
              </div>

              {/* Error Message */}
              {(error || wormholeBridge.error) && (
                <div className="flex items-center gap-2 text-destructive text-sm p-3 rounded-lg bg-destructive/10">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="break-words">{error || wormholeBridge.error}</span>
                </div>
              )}

              {/* Action Button */}
              {!isConnected ? (
                <Button disabled className="w-full h-14 text-lg">
                  Connect Wallet
                </Button>
              ) : needsChainSwitch ? (
                <Button
                  onClick={switchToSourceChain}
                  className="w-full h-14 text-lg"
                >
                  Switch to {sourceChain.name}
                </Button>
              ) : needsApproval ? (
                <Button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="w-full h-14 text-lg"
                >
                  {isApproving ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    "Approve WELEPHANT"
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleBridge}
                  disabled={!canBridge}
                  className="w-full h-14 text-lg"
                >
                  {isBridging || wormholeBridge.isBridging ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Bridging...
                    </>
                  ) : (
                    "Bridge"
                  )}
                </Button>
              )}

              {/* Bridge Details */}
              {quote && inputAmount && parseFloat(inputAmount) > 0 && (
                <div className="text-xs text-muted-foreground space-y-1.5 pt-2">
                  {/* Route Info */}
                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-1">
                      <Zap className="h-3 w-3" />
                      Route
                    </span>
                    <span className="flex items-center gap-1">
                      {quote.routeName?.replace('Route', '') || 'PortalBridge'}
                      {quote.isAutomatic && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-primary/20 text-primary">
                          Auto
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Relayer Fee</span>
                    <span>{quote.relayerFeeFormatted || 'Included in gas'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Estimated Time
                    </span>
                    <span>{quote.estimatedTime}</span>
                  </div>
                  <div className="flex justify-between pt-1.5 border-t border-muted">
                    <span>You Receive</span>
                    <span className="font-medium text-foreground">
                      {formatTokenAmount(quote.destAmount, WELEPHANT_DECIMALS)} WELEPHANT
                    </span>
                  </div>
                </div>
              )}

              {/* Pending Transfer Status */}
              {wormholeBridge.pendingTransfer && ['submitted', 'relaying'].includes(wormholeBridge.pendingTransfer.status) && (
                <div className={cn(
                  "mt-4 p-3 rounded-lg border",
                  wormholeStatus === "completed"
                    ? "bg-green-500/10 border-green-500/20"
                    : "bg-primary/10 border-primary/20"
                )}>
                  <div className="flex items-center gap-2 mb-2">
                    {wormholeStatus === "completed" ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    <span className="text-sm font-medium">
                      {wormholeStatus === "completed"
                        ? "Bridge Complete"
                        : wormholeStatus === "vaa_emitted"
                        ? "VAA Emitted - Awaiting Relay"
                        : wormholeStatus === "in_progress"
                        ? "Bridge in Progress"
                        : "Submitting..."}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {wormholeStatus === "completed"
                      ? "Your tokens have been delivered to the destination chain."
                      : wormholeStatus === "vaa_emitted"
                      ? "VAA signed by guardians. Relayers are completing the transfer."
                      : wormholeStatus === "in_progress"
                      ? "Transaction confirmed. Waiting for guardian signatures."
                      : "Your tokens are being bridged. The relayer will complete the transfer automatically."}
                  </p>
                  {wormholeBridge.pendingTransfer.txHash && (
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col gap-1.5">
                        <a
                          href={getExplorerUrl(wormholeBridge.pendingTransfer.sourceChain, wormholeBridge.pendingTransfer.txHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          View on {wormholeBridge.pendingTransfer.sourceChain.name} Explorer
                          <ExternalLink className="h-3 w-3" />
                        </a>
                        <a
                          href={getWormholescanUrl(wormholeBridge.pendingTransfer.txHash)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          Track on Wormholescan
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                      {wormholeStatus === "completed" ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearPending}
                          className="h-7 px-2 text-xs"
                        >
                          Clear
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground capitalize">
                          {wormholeStatus.replace("_", " ")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Info Card */}
        {!showHistory && (
          <Card className="mt-4">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-medium">About Wormhole Bridge</h3>
                <div className="flex items-center gap-1.5 text-xs">
                  <div className={cn(
                    "w-2 h-2 rounded-full",
                    wormholeBridge.isInitialized ? "bg-green-500" : "bg-yellow-500 animate-pulse"
                  )} />
                  <span className="text-muted-foreground">
                    {wormholeBridge.isInitialized ? 'Ready' : 'Initializing...'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Bridge WELEPHANT between Base and BNB Chain using Wormhole's secure cross-chain messaging.
                Transfers are processed automatically by relayers and typically complete in ~15 minutes.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </>
  )
}

// Bridge History View
function BridgeHistoryView({ history }: { history: BridgeHistoryItem[] }) {
  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
        <p>No bridge history yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {history.map((item) => (
        <a
          key={item.txHash}
          href={getWormholescanUrl(item.txHash)}
          target="_blank"
          rel="noopener noreferrer"
          className="block p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
        >
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ChainIcon chainId={item.sourceChainId} />
              <ArrowDown className="h-3 w-3 text-muted-foreground -rotate-90" />
              <ChainIcon chainId={item.destChainId} />
            </div>
            <span className="text-xs text-muted-foreground">
              {formatRelativeTime(item.timestamp)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-medium">
              {formatHistoryAmount(item.amount)} WELEPHANT
            </span>
            <ExternalLink className="h-3 w-3 text-muted-foreground" />
          </div>
        </a>
      ))}
    </div>
  )
}

// Chain Display Component
function ChainDisplay({ chain }: { chain: ChainConfig }) {
  return (
    <div className="flex items-center gap-2">
      <ChainIconByConfig chain={chain} />
      <span className="font-medium">{chain.name}</span>
    </div>
  )
}

// Chain Icon by config Component
function ChainIconByConfig({ chain, className }: { chain: ChainConfig; className?: string }) {
  const logo = chain.id === BASE_CHAIN.id ? baseLogo : bnbLogo
  return (
    <img
      src={logo}
      alt={chain.name}
      className={cn("w-5 h-5", className)}
    />
  )
}

// Chain Icon by ID Component
function ChainIcon({ chainId, className }: { chainId: number; className?: string }) {
  const logo = chainId === BASE_CHAIN.id ? baseLogo : bnbLogo
  const name = chainId === BASE_CHAIN.id ? 'Base' : 'BNB'
  return (
    <img
      src={logo}
      alt={name}
      className={cn("w-4 h-4", className)}
    />
  )
}

// Token Badge Component
function TokenBadge() {
  return (
    <div className="flex items-center gap-2">
      <img src={welephantLogo} alt="WELEPHANT" className="w-6 h-6" />
      <span className="font-medium">WELEPHANT</span>
    </div>
  )
}

// Helper functions
function formatHistoryAmount(amount: string): string {
  const value = parseFloat(amount)
  if (value === 0) return "0"
  if (value < 0.001) return "<0.001"
  if (value < 1000) return value.toFixed(3).replace(/\.?0+$/, '')
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 2,
  }).format(value)
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now()
  const diff = now - timestamp
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 1) return `${hours}h ago`

  // After an hour, show date and time
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function getExplorerUrl(chain: ChainConfig, txHash: string): string {
  const baseUrl = chain.blockExplorerUrls[0]
  return `${baseUrl}tx/${txHash}`
}

function getWormholescanUrl(txHash: string): string {
  return `https://wormholescan.io/#/tx/${txHash}`
}
